import {Component, inject, OnInit} from '@angular/core';
import {UserDto} from "../../models/admin_owner/user/user-dto";
import {UserHttpService} from "../x-http-requests/user-http.service";
import {Router} from "@angular/router";
import {LoadingComponent} from "../../_modules/x-components/loading/loading.component";
import {FormsModule} from "@angular/forms";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {AlertLevel} from "../../enums/alert-level";
import {
  describeAdministrationError, PASSWORD_CONFIRMATION_MISMATCH
} from "../../_shared/errors/administration-errors";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  imports: [
    FormsModule,
    LoadingComponent
  ],
  styleUrls: ['./user.component.scss']
})
export class AdminUserComponent implements OnInit {
  private readonly userHttpService = inject(UserHttpService);
  private readonly router = inject(Router);
  private readonly alerts = inject(GlobalAlertService);

  users?: UserDto[];
  filtered_users?: UserDto[];
  name: string = "";
  password: string = "";
  cPassword: string = "";
  searchText: string = "";
  creator: boolean = false;
  admin: boolean = false;
  /**
   * Meta-role creation flag for the standalone, read-only **Operator** ("Betriebsleitung") baseline: live
   * service screens only, no management surface. Meta roles are filtered out of the role screen, so this
   * toggle — at creation time — is the only way to hand the role out; it cannot be granted or revoked later.
   *
   * Mutually exclusive with {@link admin}: both are *baseline* roles, held instead of one another, so the
   * template disables whichever switch is not set while the other is on (the server rejects the combination
   * too — the UI is not the security boundary).
   */
  operator: boolean = false;
  login: boolean = true;

  constructor() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  AssignCopy(): void {
    this.filtered_users = Object.assign([], this.users);
  }

  FilterItems(value: string) {
    this.searchText = value;
    if (!value) this.AssignCopy();
    else {
      this.filtered_users = Object.assign([], this.users?.filter(user =>
        user.name.toLowerCase().indexOf(value.toLowerCase()) > -1
      ));
    }
  }

  GetLoginUsers(login: boolean): UserDto[] {
    return Object.assign([], this.filtered_users?.filter(user =>
      user.login == login
    ))
  }

  LoadData() {
    this.userHttpService.list().subscribe({
      next: x => {
        this.users = x;
        this.AssignCopy();
      },
      error: error => {
        console.error(error);
        this.fail(error, "Die Benutzerliste konnte nicht geladen werden. Bitte laden Sie die Seite neu.");
      }
    });
  }

  AddUser() {
    if (this.cPassword != this.password) {
      // Refused here, before anything is sent: the server answers a blank/short password with a bare 400 that
      // says nothing about the confirmation field, so this check owns the wording.
      this.fail(undefined, PASSWORD_CONFIRMATION_MISMATCH);
      return;
    }
    this.userHttpService.add(this.name, this.password, this.admin, this.operator, this.login).subscribe({
      next: x => {
        if (this.users == undefined) this.users = [];
        this.users.splice(0, 0, x);
        this.FilterItems(this.searchText);
        this.name = "";
        this.password = "";
        this.cPassword = "";
        this.admin = false;
        this.operator = false;
      },
      error: error => {
        console.error(error);
        this.fail(error, "Der Benutzer konnte nicht angelegt werden. Bitte prüfen Sie Name und Passwort.");
      }
    })
  }

  RemoveUser(user: UserDto) {
    let confirmation = confirm("Are you sure you want to delete the following user:\n" + user.name + "\n" + user.id);
    if (!confirmation) return;
    this.userHttpService.delete(user.id).subscribe({
      next: () => {
        this.users?.splice(this.users?.indexOf(user), 1);
        this.FilterItems(this.searchText);
      },
      error: error => {
        console.error(error);
        this.fail(error, "Der Benutzer konnte nicht gelöscht werden. Bitte laden Sie die Seite neu.");
      }
    })
  }

  /**
   * Surfaces a refusal as a German sentence through the global alert banner.
   *
   * Replaces `ErrorBannerComponent`, which rendered `Error · {{ statusText }}` and discarded the structured
   * `ApiError` entirely — so `E_USER_NAME_RESERVED_PREFIX`, the one code this screen's create endpoint really
   * answers, reached the administrator as `Error · Bad Request`. The sentence is mapped from the error **code**;
   * the backend's English `description` is never rendered.
   *
   * `err` is `undefined` for the locally-detected refusals, where `fallback` is the whole message.
   */
  private fail(err: unknown, fallback: string): void {
    this.alerts.createAndShow("Fehler", describeAdministrationError(err, fallback), AlertLevel.error, 8000);
  }

  ngOnInit(): void {
    this.LoadData();
  }
}
